import { useMemo } from 'react';

import { Button, Divider, Stack, styled } from '@mui/material';

import { AppModal, BaseModalProps } from '@/components';
import { useCFSContext } from '@/hooks';
import { AssignedUnitShiftStatus, IAgency } from '@/models';

const CFSIncidentCodeUnassignModalRoot = styled(AppModal)(({ theme }) => ({
  '& .modal-content': {
    position: 'absolute',
    minWidth: 500,
    padding: 20,
    borderRadius: 8,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 600,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 600,
    },

    '& .btn-close': {
      position: 'absolute',
      right: 8,
      top: 8,
      color: theme.palette.error.main,
    },
    '& .separator': {
      marginTop: 20,
      marginBottom: 20,
    },
  },
}));

interface CFSIncidentCodeUnassignModalProps
  extends Omit<BaseModalProps, 'onClose'> {
  onSubmit: () => void;
  open: boolean;
  onClose: () => void;
  removedAgencies: IAgency[];
}

export const CFSIncidentCodeUnassignModal = ({
  removedAgencies,
  onClose,
  open,
  onSubmit,
}: CFSIncidentCodeUnassignModalProps) => {
  const { cfs } = useCFSContext();

  const removedAgenciesIds = removedAgencies.map((agc) => agc._id);

  const units = useMemo(() => {
    if (!cfs?.assignedUnitShifts) return [];

    return cfs.assignedUnitShifts
      .filter(
        (au) =>
          removedAgenciesIds.includes(au?.unitShift?.unit?.agency) &&
          au.status !== AssignedUnitShiftStatus.CANCELED &&
          au.status !== AssignedUnitShiftStatus.COMPLETED,
      )
      .map((au) => au?.unitShift?.unit?.name);
  }, [cfs, removedAgenciesIds]);

  const agencyName = removedAgencies.map((agc) => agc.name).join(' ');

  return (
    <CFSIncidentCodeUnassignModalRoot
      onClose={onClose}
      open={open}
      title="Incident Code Warning!"
      hideCloseButton
    >
      <Stack rowGap={2} divider={<Divider />}>
        <Stack>
          <Stack>
            Due to incident code restrictions, we will unassign the agency{' '}
            {agencyName} from the CFS.
          </Stack>

          {units?.length ? (
            <>
              <Stack>
                Assignments to the following units will be cancelled:
              </Stack>
              <Stack>
                {units?.map((u) => (
                  <Stack key={u} sx={{ px: 2 }}>
                    • {u}
                  </Stack>
                ))}
              </Stack>
            </>
          ) : (
            <></>
          )}

          <Stack>
            Do you still want to unassign {agencyName} from this CFS?
          </Stack>
        </Stack>

        <Stack flexDirection="row" justifyContent="space-between">
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onSubmit}>
            Unassign
          </Button>
        </Stack>
      </Stack>
    </CFSIncidentCodeUnassignModalRoot>
  );
};
